import React, { useState, useEffect, useMemo } from 'react';
import axios from '../api/api';
import {
    Box, Typography, Paper, Grid, Button, TextField, Select, MenuItem,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Alert, IconButton, CircularProgress, Tooltip, InputLabel, FormControl,
    TablePagination, InputAdornment
} from '@mui/material';
import {
    Save as SaveIcon,
    CloudUpload as CloudUploadIcon,
    AutoFixHigh as MagicIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Warning as WarningIcon,
    Search as SearchIcon,
    Clear as ClearIcon
} from '@mui/icons-material';

// ====================================================================
// CONTEÚDO AUXILIAR DO SISTEMA (Substitui imports externos)
// ====================================================================

// 1. Definições de API
export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = {
    get: (endpoint) => axios.get(`${API_URL}${endpoint}`),
    post: (endpoint, data) => axios.post(`${API_URL}${endpoint}`, data),
};

const getConfig = (key) => api.get(`/config/${key}`);
const saveConfig = (key, data) => api.post(`/config/${key}`, data);

const uploadFile = async (file, fileType) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API_URL}/upload_${fileType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};

// Novas funções de API para gerenciamento de listas
const addConfigRecord = (key, record) => {
    return api.post('/config_list/add', { key, record });
};

const deleteConfigRecord = (key, primary_key, value) => {
    return api.post('/config_list/delete', { key, primary_key, value });
};

const deduplicateConfigKey = (key, primary_key) => {
    return api.post('/config_list/deduplicate', { key, primary_key });
};

// ====================================================================

const CLASSIFICACOES = ['Receita', 'Custo', 'Despesa', 'Ativo', 'Passivo', 'Patrimônio Líquido', 'Não Classificado'];
const CLASSIFICACOES_DRE = [
    'Receita Operacional Bruta',
    '(-) Deduções da Receita Bruta',
    '(-) Custos Variáveis',
    '(-) Despesas Operacionais / Fixas',
    'Nenhuma'
];

const PlanoContasPage = () => {
    console.log("DEBUG: Renderizando PlanoContasPage");
    const [planoContas, setPlanoContas] = useState([]);
    console.log("DEBUG: planoContas state:", planoContas);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [fileSelected, setFileSelected] = useState(null);

    // Estado para a nova linha
    const [newConta, setNewConta] = useState('');
    const [newDescricao, setNewDescricao] = useState('');
    const [newTipo, setNewTipo] = useState('Não Classificado');
    const [newClassificacaoDRE, setNewClassificacaoDRE] = useState('Nenhuma');
    const [newVigenciaInicial, setNewVigenciaInicial] = useState('');
    const [newVigenciaFinal, setNewVigenciaFinal] = useState('');
    const [showAddRow, setShowAddRow] = useState(false);

    const [missingAccounts, setMissingAccounts] = useState([]);
    const [pendingAccounts, setPendingAccounts] = useState([]);

    // Estado de Busca e Paginação para alta performance
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);

    const filteredPlanoContas = useMemo(() => {
        if (!searchTerm.trim()) return planoContas;
        const term = searchTerm.toLowerCase();
        return planoContas.filter(item => 
            (item.Conta && String(item.Conta).toLowerCase().includes(term)) ||
            (item.Descricao && String(item.Descricao).toLowerCase().includes(term)) ||
            (item.Tipo && String(item.Tipo).toLowerCase().includes(term)) ||
            (item.ClassificacaoDRE && String(item.ClassificacaoDRE).toLowerCase().includes(term))
        );
    }, [planoContas, searchTerm]);

    const paginatedPlanoContas = useMemo(() => {
        return filteredPlanoContas.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [filteredPlanoContas, page, rowsPerPage]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    useEffect(() => {
        fetchPlanoContas();
        fetchMissingAccounts();
    }, []);

    useEffect(() => {
        if (missingAccounts.length > 0) {
            const filtered = missingAccounts.filter(
                ma => ma && !planoContas.some(c => c.Conta && c.Conta.toUpperCase() === ma.toUpperCase())
            );
            setPendingAccounts(filtered);
        } else {
            setPendingAccounts([]);
        }
    }, [missingAccounts, planoContas]);


    const fetchPlanoContas = async (showSuccess = false) => {
        setLoading(true);
        setError(null);
        try {
            const res = await getConfig('plano_contas_data'); // Busca os dados, não o arquivo
            console.log("DEBUG: fetchPlanoContas response:", res);

            let responseData = res.data;
            if (typeof responseData === 'string') {
                try {
                    responseData = JSON.parse(responseData);
                } catch (e) {
                    console.error("DEBUG: Failed to parse response data in fetch:", e);
                }
            }
            console.log("DEBUG: fetchPlanoContas parsed data:", responseData);

            const data = Array.isArray(responseData) ? responseData.map(item => ({
                Conta: item.Conta || item.conta || '',
                Descricao: item.Descricao || item.descricao || '',
                Tipo: item.Tipo || item.tipo || 'Não Classificado',
                ClassificacaoDRE: item.ClassificacaoDRE || item.classificacaoDRE || 'Nenhuma',
                VigenciaInicial: item.VigenciaInicial || item.vigenciaInicial || '',
                VigenciaFinal: item.VigenciaFinal || item.vigenciaFinal || ''
            })) : [];
            setPlanoContas(data);
            if (showSuccess) {
                setSuccess("Dados recarregados com sucesso.");
                setTimeout(() => setSuccess(null), 3000);
            }
        } catch (err) {
            console.error("DEBUG: Error in fetchPlanoContas:", err);
            setError('Erro ao carregar o Plano de Contas. Verifique o console do servidor.');
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async () => {
        if (!fileSelected) {
            setError("Selecione um arquivo CSV para upload.");
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await uploadFile(fileSelected, 'plano_contas');
            setSuccess(`Arquivo '${fileSelected.name}' importado com sucesso. Os dados foram limpos e salvos.`);
            setFileSelected(null); // Limpa o seletor de arquivo
            console.log("DEBUG: Calling fetchPlanoContas after upload");
            fetchPlanoContas(); // Recarrega os dados do DB (que agora estão limpos)
        } catch (err) {
            const errorMsg = err.response?.data?.error || err.message;
            setError(`Erro ao importar o arquivo: ${errorMsg}`);
        } finally {
            setSaving(false);
            setTimeout(() => setSuccess(null), 3000);
        }
    };

    const fetchMissingAccounts = async () => {
        try {
            const res = await api.get('/plano_contas/faltantes');
            if (res.data && res.data.nao_cadastradas) {
                setMissingAccounts(res.data.nao_cadastradas);
            }
        } catch (err) {
            console.error('Erro ao carregar contas faltantes:', err);
        }
    };

    const handleQuickAddAccount = async (conta) => {
        if (planoContas.some(c => c.Conta.toUpperCase() === conta.toUpperCase())) {
            setError('Esta conta já está cadastrada.');
            setTimeout(() => setError(null), 3000);
            return;
        }

        setSaving(true);
        try {
            const newRecord = {
                Conta: conta,
                Descricao: '',
                Tipo: 'Não Classificado',
                ClassificacaoDRE: 'Nenhuma',
                VigenciaInicial: '',
                VigenciaFinal: ''
            };
            await addConfigRecord('plano_contas_data', newRecord);
            setSuccess(`Conta '${conta}' adicionada com sucesso!`);
            fetchPlanoContas();
        } catch (err) {
            console.error("Erro ao adicionar conta:", err);
            setError("Erro ao adicionar conta rápida: " + (err.response?.data?.error || err.message));
        } finally {
            setSaving(false);
            setTimeout(() => setSuccess(null), 3000);
        }
    };

    const handleAddNewRecord = async () => {
        if (!newConta) {
            setError("O campo 'Conta' é obrigatório.");
            return;
        }
        setSaving(true);
        try {
            const newRecord = {
                Conta: newConta,
                Descricao: newDescricao,
                Tipo: newTipo,
                ClassificacaoDRE: newClassificacaoDRE,
                VigenciaInicial: newVigenciaInicial,
                VigenciaFinal: newVigenciaFinal
            };
            await addConfigRecord('plano_contas_data', newRecord);
            setSuccess("Conta adicionada com sucesso.");
            setNewConta('');
            setNewDescricao('');
            setNewTipo('Não Classificado');
            setNewClassificacaoDRE('Nenhuma');
            setNewVigenciaInicial('');
            setNewVigenciaFinal('');
            setShowAddRow(false);
            fetchPlanoContas();
        } catch (err) {
            console.error("Erro ao adicionar conta:", err);
            setError("Erro ao adicionar conta: " + (err.response?.data?.error || err.message));
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteRecord = async (conta) => {
        if (!window.confirm(`Tem certeza que deseja excluir a conta ${conta}?`)) return;
        setSaving(true);
        try {
            await deleteConfigRecord('plano_contas_data', 'Conta', conta);
            setSuccess("Conta excluída com sucesso.");
            fetchPlanoContas();
        } catch (err) {
            console.error("Erro ao excluir conta:", err);
            setError("Erro ao excluir conta: " + (err.response?.data?.error || err.message));
        } finally {
            setSaving(false);
        }
    };

    const handleDeduplicate = async () => {
        if (!window.confirm("Isso irá remover registros duplicados baseados na coluna 'Conta'. Deseja continuar?")) return;
        setSaving(true);
        try {
            const res = await deduplicateConfigKey('plano_contas_data', 'Conta');
            let removedCount = 0;
            if (res.data && typeof res.data.removed_count !== 'undefined') {
                removedCount = res.data.removed_count;
            } else if (typeof res.data === 'string') {
                try {
                    const parsed = JSON.parse(res.data);
                    removedCount = parsed.removed_count || 0;
                } catch (e) {
                    console.error("Erro ao parsear resposta de deduplicação:", e);
                }
            }

            setSuccess(`Deduplicação concluída. ${removedCount} registros removidos.`);
            fetchPlanoContas();
        } catch (err) {
            console.error("Erro na deduplicação:", err);
            setError("Erro ao deduplicar: " + (err.response?.data?.error || err.message));
        } finally {
            setSaving(false);
        }
    };

    const handleSavePlanoContas = async () => {
        // Esta função salvaria todo o array, mas estamos usando edição linha a linha ou via API específica.
        // Se for para salvar tudo de uma vez (não recomendado para grandes listas):
        setSaving(true);
        try {
            await saveConfig('plano_contas_data', planoContas);
            setSuccess("Alterações salvas com sucesso.");
        } catch (err) {
            console.error("Erro ao salvar plano de contas:", err);
            setError("Erro ao salvar alterações: " + (err.response?.data?.error || err.message));
        } finally {
            setSaving(false);
        }
    };

    const handleUpdateRecord = (conta, field, value) => {
        const updatedList = planoContas.map(item => {
            if (item.Conta === conta) {
                return { ...item, [field]: value };
            }
            return item;
        });
        setPlanoContas(updatedList);
    };

    if (loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <CircularProgress />
        </Box>
    );

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                Configuração do Plano de Contas
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                Gerencie as contas e suas classificações (Receita, Custo ou Despesa) que serão usadas no motor de rateio.
            </Typography>

            {/* Mensagens de Feedback */}
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}


            {/* --- Seção de Ações --- */}
            <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>Ações de Gerenciamento</Typography>
                <Grid container spacing={2} alignItems="center">
                    {/* 1. Upload */}
                    <Grid item xs={12} md={5}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<CloudUploadIcon />}
                            >
                                Selecionar CSV
                                <input
                                    type="file"
                                    hidden
                                    accept=".csv"
                                    onChange={(e) => setFileSelected(e.target.files[0])}
                                />
                            </Button>
                            <Typography variant="caption" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
                                {fileSelected ? fileSelected.name : 'Nenhum arquivo selecionado'}
                            </Typography>
                            <Button
                                variant="contained"
                                color="info"
                                onClick={handleFileUpload}
                                disabled={!fileSelected || saving}
                                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                            >
                                {saving ? 'Importando...' : 'Importar'}
                            </Button>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Box sx={{ width: 1, height: 40, borderLeft: 1, borderColor: 'divider', display: { xs: 'none', md: 'block' } }} />
                    </Grid>

                    {/* 2. Ações Manuais */}
                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                color="success"
                                onClick={() => setShowAddRow(!showAddRow)}
                                startIcon={<AddIcon />}
                            >
                                Adicionar Conta
                            </Button>
                            <Button
                                variant="contained"
                                color="warning"
                                onClick={handleDeduplicate}
                                disabled={saving}
                                startIcon={<MagicIcon />}
                            >
                                Limpar Duplicadas
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSavePlanoContas}
                                disabled={saving}
                                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                            >
                                {saving ? 'Salvando...' : 'Salvar Alterações'}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* --- Contas Não Cadastradas --- */}
            {pendingAccounts.length > 0 && (
                <Paper sx={{ p: 3, mb: 4, bgcolor: 'warning.light', border: 2, borderColor: 'warning.main' }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', color: 'warning.dark' }}>
                        <WarningIcon sx={{ mr: 1 }} />
                        Contas Não Cadastradas no Plano de Contas ({pendingAccounts.length})
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, color: 'warning.dark' }}>
                        As contas abaixo foram encontradas no balancete importado, mas ainda não estão no Plano de Contas. Adicione-as para poder classificar.
                    </Typography>
                    <Grid container spacing={2}>
                        {pendingAccounts.map((conta, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'background.paper', p: 1.5, borderRadius: 1, boxShadow: 1, border: 1, borderColor: 'warning.light' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', pr: 2 }} title={conta}>
                                        {conta}
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={() => handleQuickAddAccount(conta)}
                                        disabled={saving}
                                    >
                                        Adicionar
                                    </Button>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>
            )}

            {/* --- Tabela de Classificação --- */}
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Plano de Contas Atual ({filteredPlanoContas.length} de {(planoContas || []).length} Contas)
                    </Typography>
                    <TextField
                        size="small"
                        placeholder="Buscar por conta ou descrição..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setPage(0);
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => { setSearchTerm(''); setPage(0); }}>
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null
                        }}
                        sx={{ width: { xs: '100%', sm: 320 } }}
                    />
                </Box>
                <TableContainer sx={{ maxHeight: '60vh' }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Conta</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Descrição</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Tipo</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Classificação DRE</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Vigência Inicial</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Vigência Final</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', width: '10%' }}>Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {/* --- Linha de Adição --- */}
                            {showAddRow && (
                                <TableRow sx={{ backgroundColor: 'success.light' }}>
                                    <TableCell>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={newConta}
                                            onChange={(e) => setNewConta(e.target.value)}
                                            placeholder="Digite o nome exato da nova conta"
                                            variant="outlined"
                                            sx={{ backgroundColor: 'background.paper' }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={newDescricao}
                                            onChange={(e) => setNewDescricao(e.target.value)}
                                            placeholder="Descrição da conta"
                                            variant="outlined"
                                            sx={{ backgroundColor: 'background.paper' }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <FormControl fullWidth size="small">
                                            <Select
                                                value={newTipo}
                                                onChange={(e) => setNewTipo(e.target.value)}
                                                sx={{ backgroundColor: 'background.paper' }}
                                            >
                                                {CLASSIFICACOES.map(tipo => (
                                                    <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell>
                                        <FormControl fullWidth size="small">
                                            <Select
                                                value={newClassificacaoDRE}
                                                onChange={(e) => setNewClassificacaoDRE(e.target.value)}
                                                sx={{ backgroundColor: 'background.paper' }}
                                            >
                                                {CLASSIFICACOES_DRE.map(tipo => (
                                                    <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={newVigenciaInicial}
                                            onChange={(e) => setNewVigenciaInicial(e.target.value)}
                                            placeholder="YYYY-MM"
                                            variant="outlined"
                                            sx={{ backgroundColor: 'background.paper' }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={newVigenciaFinal}
                                            onChange={(e) => setNewVigenciaFinal(e.target.value)}
                                            placeholder="Vazio=indefinido"
                                            variant="outlined"
                                            sx={{ backgroundColor: 'background.paper' }}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Button
                                            variant="contained"
                                            color="success"
                                            size="small"
                                            onClick={handleAddNewRecord}
                                            disabled={saving}
                                        >
                                            Salvar
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            )}

                            {/* --- Linhas de Dados Paginas --- */}
                            {paginatedPlanoContas.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                        Nenhuma conta encontrada.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedPlanoContas.map((item, index) => (
                                    <TableRow key={item.Conta || index} hover>
                                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'medium' }}>{item.Conta}</TableCell>
                                        <TableCell>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                variant="standard"
                                                value={item.Descricao || ''}
                                                onChange={(e) => handleUpdateRecord(item.Conta, 'Descricao', e.target.value)}
                                                placeholder="Descrição"
                                                InputProps={{ disableUnderline: true }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <FormControl fullWidth size="small" variant="standard">
                                                <Select
                                                    value={CLASSIFICACOES.includes(item.Tipo) ? item.Tipo : 'Não Classificado'}
                                                    onChange={(e) => handleUpdateRecord(item.Conta, 'Tipo', e.target.value)}
                                                    disableUnderline
                                                >
                                                    {CLASSIFICACOES.map(tipo => (
                                                        <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell>
                                            <FormControl fullWidth size="small" variant="standard">
                                                <Select
                                                    value={CLASSIFICACOES_DRE.includes(item.ClassificacaoDRE) ? item.ClassificacaoDRE : 'Nenhuma'}
                                                    onChange={(e) => handleUpdateRecord(item.Conta, 'ClassificacaoDRE', e.target.value)}
                                                    disableUnderline
                                                >
                                                    {CLASSIFICACOES_DRE.map(tipo => (
                                                        <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                variant="standard"
                                                value={item.VigenciaInicial || ''}
                                                onChange={(e) => handleUpdateRecord(item.Conta, 'VigenciaInicial', e.target.value)}
                                                placeholder="YYYY-MM"
                                                InputProps={{ disableUnderline: true }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                variant="standard"
                                                value={item.VigenciaFinal || ''}
                                                onChange={(e) => handleUpdateRecord(item.Conta, 'VigenciaFinal', e.target.value)}
                                                placeholder="Vazio=indefinido"
                                                InputProps={{ disableUnderline: true }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                onClick={() => handleDeleteRecord(item.Conta)}
                                                disabled={saving}
                                                color="error"
                                                size="small"
                                                title={`Excluir conta ${item.Conta}`}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={filteredPlanoContas.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[25, 50, 100, 250, 500]}
                    labelRowsPerPage="Itens por página:"
                    labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`}
                />
            </Paper>
        </Box>
    );
};

export default PlanoContasPage;
